// Spec 37 — Certificaat-aanvragen (medewerker dient in, backoffice keurt goed).
//
// Gedeelde DTO's + request-schemas (API <-> portal/backoffice). Eén bron: de API
// parseert ermee én het portal-form valideert er client-side mee vóór verzenden,
// zodat het form niets kan sturen dat de API weigert (CLAUDE.md §Validatie).

import { z } from 'zod'
import type {
  CertificaatExtractieDTO,
  CertificaatMatchOnderbouwing,
  MogelijkDubbelDTO,
} from './certificaat-mailbox'

export type CertificaatAanvraagStatus =
  | 'INGEDIEND'
  | 'GOEDGEKEURD'
  | 'AFGEKEURD'
  | 'INGETROKKEN'

export const CERTIFICAAT_AANVRAAG_STATUS_LABELS: Record<CertificaatAanvraagStatus, string> = {
  INGEDIEND: 'Ingediend',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgewezen',
  INGETROKKEN: 'Ingetrokken',
}

// batch-63 certificaat-mailbox (FO-469) — herkomst van een aanvraag.
export const CERTIFICAAT_AANVRAAG_BRONNEN = ['PORTAL', 'MAILBOX', 'HANDMATIG'] as const
export type CertificaatAanvraagBron = (typeof CERTIFICAAT_AANVRAAG_BRONNEN)[number]

export const CERTIFICAAT_AANVRAAG_BRON_LABELS: Record<CertificaatAanvraagBron, string> = {
  PORTAL: 'Portal (medewerker)',
  MAILBOX: 'Certificaat-mailbox',
  HANDMATIG: 'Handmatig (HR)',
}

/**
 * batch-63 certificaat-mailbox (FO-469 §7/§15) — herkomst van een
 * certificaatversie. Afgeleid (geen eigen kolom): de aanvraag die de versie
 * opleverde, anders de bron in de aanmaak-audit. Zie `bepaalCertificaatHerkomst`
 * in @streep/utils.
 */
export const CERTIFICAAT_HERKOMSTEN = ['MAILBOX', 'PORTAL', 'OPLEIDING', 'HANDMATIG'] as const
export type CertificaatHerkomst = (typeof CERTIFICAAT_HERKOMSTEN)[number]

export const CERTIFICAAT_HERKOMST_LABELS: Record<CertificaatHerkomst, string> = {
  MAILBOX: 'Certificaat-mailbox',
  PORTAL: 'Portal (medewerker)',
  OPLEIDING: 'Opleiding afgerond',
  HANDMATIG: 'Handmatig (HR)',
}

/**
 * batch-63 certificaat-mailbox — de mailbox-herkomst van een aanvraag, alleen in
 * de backoffice-detailview (vergelijkweergave). Nooit in een portal-DTO: daar
 * staat alleen `bron`.
 */
export interface CertificaatAanvraagHerkomstDTO {
  ingestId: string
  itemId: string | null
  mailbox: string
  afzender: string | null
  onderwerp: string | null
  /** ISO */
  ontvangenOp: string | null
  extractie: CertificaatExtractieDTO | null
  onderbouwing: CertificaatMatchOnderbouwing | null
  /** De vervaldatum is berekend uit een geldigheidsduur. */
  expiresAfgeleid: boolean
  /** Het bestand is een per deelnemer gesplitst deel van een bundel. */
  isBundelDeel: boolean
}

export interface CertificaatAanvraagDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  employeeNumber: string | null
  certificateTypeId: string
  certificateTypeNaam: string | null
  certificateTypeCode: string | null
  // ISO-datums (Europe/Amsterdam getoond in de UI).
  issuedAt: string
  geldigVanaf: string | null
  expiresAt: string | null
  certificaatNummer: string | null
  uitgever: string | null
  notes: string | null
  status: CertificaatAanvraagStatus
  beoordeeldDoorId: string | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  beoordelingNotitie: string | null
  resultaatCertificaatId: string | null
  bestandNaam: string | null
  heeftBestand: boolean
  mimeType: string | null
  // batch-63 certificaat-mailbox (FO-469)
  bron: CertificaatAanvraagBron
  ingestId: string | null
  opleidingDeelnameId: string | null
  /** Alleen backoffice-detail: mailbox-herkomst voor de vergelijkweergave. */
  herkomst?: CertificaatAanvraagHerkomstDTO | null
  /** Alleen backoffice-detail: lijkt op een bestaand certificaat of open aanvraag. */
  mogelijkDubbel?: MogelijkDubbelDTO[]
  /**
   * Alleen backoffice-detail: mag deze gebruiker de medewerker corrigeren
   * (HR-niveau, en nooit bij een door de medewerker zelf ingediende aanvraag).
   */
  magMedewerkerWijzigen?: boolean
  createdAt: string
  updatedAt: string
}

// ISO-datumstring (yyyy-mm-dd of volledige ISO). `Date.parse` is leidend; de API
// converteert daarna naar een echte Date.
const aanvraagDatum = z
  .string()
  .trim()
  .min(1, 'Datum is verplicht')
  .refine((s) => !Number.isNaN(Date.parse(s)), 'Ongeldige datum')

/**
 * Tekstvelden van een nieuwe aanvraag (het bewijsbestand zit in de multipart-part,
 * niet in dit JSON-schema; de route eist het bestand apart). `.nullable()` op de
 * optionele velden zodat de read-DTO (null) round-tript zonder VALIDATION_ERROR.
 */
export const CreateCertificaatAanvraagSchema = z.object({
  certificateTypeId: z.string().trim().min(1, 'Kies een certificaattype'),
  issuedAt: aanvraagDatum,
  geldigVanaf: aanvraagDatum.nullable().optional(),
  expiresAt: aanvraagDatum.nullable().optional(),
  certificaatNummer: z.string().trim().max(120, 'Maximaal 120 tekens').nullable().optional(),
  uitgever: z.string().trim().max(200, 'Maximaal 200 tekens').nullable().optional(),
  notes: z.string().trim().max(2000, 'Maximaal 2000 tekens').nullable().optional(),
})
export type CreateCertificaatAanvraagInput = z.infer<typeof CreateCertificaatAanvraagSchema>

/**
 * batch-63 certificaat-mailbox (FO-469 §D9) — velden die HR in de
 * vergelijkweergave nog kan corrigeren vóór goedkeuring. De medewerker is alleen
 * te wijzigen bij een aanvraag die NIET door de medewerker zelf is ingediend
 * (bron MAILBOX/HANDMATIG); de API bewaakt dat.
 */
export const CertificaatAanvraagCorrectieSchema = z
  .object({
    employeeId: z.string().trim().min(1, 'Kies een medewerker').optional(),
    certificateTypeId: z.string().trim().min(1, 'Kies een certificaattype').optional(),
    issuedAt: aanvraagDatum.optional(),
    geldigVanaf: aanvraagDatum.nullable().optional(),
    expiresAt: aanvraagDatum.nullable().optional(),
    certificaatNummer: z.string().trim().max(120, 'Maximaal 120 tekens').nullable().optional(),
    uitgever: z.string().trim().max(200, 'Maximaal 200 tekens').nullable().optional(),
    notes: z.string().trim().max(2000, 'Maximaal 2000 tekens').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.issuedAt && v.expiresAt && Date.parse(v.expiresAt) < Date.parse(v.issuedAt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiresAt'],
        message: 'De vervaldatum ligt vóór de uitgiftedatum',
      })
    }
    if (v.geldigVanaf && v.expiresAt && Date.parse(v.expiresAt) < Date.parse(v.geldigVanaf)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiresAt'],
        message: 'De vervaldatum ligt vóór "geldig vanaf"',
      })
    }
  })
export type CertificaatAanvraagCorrectieInput = z.infer<typeof CertificaatAanvraagCorrectieSchema>

/**
 * Goedkeuren: optionele interne notitie; de aanvraag wordt verder 1:1 het cert.
 * batch-63: plus optionele correcties (vergelijkweergave) en de expliciete
 * bevestiging bij een "mogelijk dubbel" (mailbox-aanvragen worden daarop
 * geblokkeerd tot HR bewust doorgaat).
 */
export const GoedkeurCertificaatAanvraagSchema = z.object({
  beoordelingNotitie: z.string().trim().max(2000, 'Maximaal 2000 tekens').nullable().optional(),
  correcties: CertificaatAanvraagCorrectieSchema.optional(),
  bevestigMogelijkDubbel: z.boolean().optional(),
})
export type GoedkeurCertificaatAanvraagInput = z.infer<typeof GoedkeurCertificaatAanvraagSchema>

/** Afwijzen: reden verplicht (medewerker ziet 'm). */
export const AfwijzenCertificaatAanvraagSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden van minimaal 5 tekens')
    .max(2000, 'Maximaal 2000 tekens'),
})
export type AfwijzenCertificaatAanvraagInput = z.infer<typeof AfwijzenCertificaatAanvraagSchema>
